import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Upload, Link } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AdminCars = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [useUrlInput, setUseUrlInput] = useState(true);
  
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    base_price: '',
    min_price: '',
    max_price: '',
    seats: '',
    transmission: '',
    fuel_type: '',
    image_url: '',
    featured: false
  });

  useEffect(() => {
    fetchCars();
  }, []);

  const fetchCars = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/cars`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCars(response.data);
    } catch (error) {
      console.error('Error fetching cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API_BASE_URL}/api/admin/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.imageUrl }));
        setImagePreview(response.data.imageUrl);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const submitData = {
      name: formData.name,
      brand: formData.brand,
      base_price: formData.base_price || 'Price on Request',
      min_price: formData.min_price || 'Price on Request',
      max_price: formData.max_price || 'Price on Request',
      seats: parseInt(formData.seats) || 0,
      transmission: formData.transmission,
      fuel_type: formData.fuel_type,
      image_url: formData.image_url || '',
      featured: formData.featured || false
    };

    try {
      if (editingCar) {
        await axios.put(`${API_BASE_URL}/api/admin/cars/${editingCar.id}`, submitData, config);
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/cars`, submitData, config);
      }
      fetchCars();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving car:', error);
      alert(error.response?.data?.error || 'Error saving car. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this car?')) {
      const token = localStorage.getItem('adminToken');
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/cars/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchCars();
      } catch (error) {
        console.error('Error deleting car:', error);
        alert('Error deleting car. Please try again.');
      }
    }
  };

  const handleEdit = (car) => {
    setEditingCar(car);
    setFormData({
      name: car.name || '',
      brand: car.brand || '',
      base_price: car.base_price || '',
      min_price: car.min_price || '',
      max_price: car.max_price || '',
      seats: car.seats || '',
      transmission: car.transmission || '',
      fuel_type: car.fuel_type || '',
      image_url: car.image_url || '',
      featured: car.featured || false
    });
    setImagePreview(car.image_url || '');
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingCar(null);
    setFormData({
      name: '',
      brand: '',
      base_price: '',
      min_price: '',
      max_price: '',
      seats: '',
      transmission: '',
      fuel_type: '',
      image_url: '',
      featured: false
    });
    setImagePreview('');
    setUseUrlInput(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Manage Cars</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add New Car</span>
        </button>
      </div>

      {/* Cars Table */}
      <div className="bg-dark-900 rounded-lg overflow-hidden border border-dark-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Image</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Brand</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Price</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Seats</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Featured</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {cars.map((car) => (
                <tr key={car.id} className="hover:bg-dark-800 transition">
                  <td className="px-4 py-3 text-sm text-gray-400">{car.id}</td>
                  <td className="px-4 py-3">
                    <img src={car.image_url} alt={car.name} className="w-12 h-12 object-cover rounded" />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-white">{car.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{car.brand}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {car.base_price || car.min_price ? `${car.base_price || car.min_price}` : 'Price on Request'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">{car.seats}</td>
                  <td className="px-4 py-3">
                    {car.featured ? (
                      <span className="bg-orange-500/20 text-orange-400 px-2 py-1 rounded-full text-xs">Yes</span>
                    ) : (
                      <span className="bg-dark-700 text-gray-400 px-2 py-1 rounded-full text-xs">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex space-x-2">
                      <button onClick={() => handleEdit(car)} className="text-blue-400 hover:text-blue-300">
                        <Edit2 size={18} />
                      </button>
                      <button onClick={() => handleDelete(car.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto border border-dark-700">
            <div className="p-4 border-b border-dark-700 flex justify-between items-center sticky top-0 bg-dark-800">
              <h2 className="text-xl font-bold text-white">
                {editingCar ? 'Edit Car' : 'Add New Car'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Car Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Brand *</label>
                <input
                  type="text"
                  required
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Base Price *</label>
                <input
                  type="text"
                  required
                  value={formData.base_price}
                  onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                  placeholder="e.g., 150,000 RWF or Call for Price"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Min Price</label>
                  <input
                    type="text"
                    value={formData.min_price}
                    onChange={(e) => setFormData({ ...formData, min_price: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                    placeholder="e.g., 120,000 RWF"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Max Price</label>
                  <input
                    type="text"
                    value={formData.max_price}
                    onChange={(e) => setFormData({ ...formData, max_price: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                    placeholder="e.g., 200,000 RWF"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Seats *</label>
                  <input
                    type="number"
                    required
                    value={formData.seats}
                    onChange={(e) => setFormData({ ...formData, seats: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Transmission</label>
                  <select
                    required
                    value={formData.transmission}
                    onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                  >
                    <option value="">Select</option>
                    <option value="Automatic">Automatic</option>
                    <option value="Manual">Manual</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Fuel Type</label>
                  <select
                    required
                    value={formData.fuel_type}
                    onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                  >
                    <option value="">Select</option>
                    <option value="Petrol">Petrol</option>
                    <option value="Diesel">Diesel</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2 mt-6">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-orange-500"
                  />
                  <label htmlFor="featured" className="text-sm text-gray-300">Featured Car</label>
                </div>
              </div>

              {/* Image Upload */}
              <div className="border-t border-dark-700 pt-4">
                <label className="block text-sm font-medium mb-2 text-gray-300">Car Image</label>
                <div className="flex space-x-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setUseUrlInput(true)}
                    className={`flex-1 py-2 rounded-lg text-sm ${useUrlInput ? 'bg-orange-500 text-white' : 'bg-dark-700 text-gray-400'}`}
                  >
                    Use URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseUrlInput(false)}
                    className={`flex-1 py-2 rounded-lg text-sm ${!useUrlInput ? 'bg-orange-500 text-white' : 'bg-dark-700 text-gray-400'}`}
                  >
                    Upload File
                  </button>
                </div>
                {useUrlInput ? (
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => {
                      setFormData({ ...formData, image_url: e.target.value });
                      setImagePreview(e.target.value);
                    }}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                    placeholder="https://example.com/image.jpg"
                  />
                ) : (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white file:mr-2 file:py-1 file:px-3 file:rounded file:bg-orange-500 file:text-white"
                    disabled={uploading}
                  />
                )}
                {uploading && <p className="text-gray-400 text-sm mt-1">Uploading...</p>}
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="mt-3 w-24 h-24 object-cover rounded" />
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="submit" className="flex-1 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition">
                  {editingCar ? 'Update' : 'Add'} Car
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-dark-600 py-2 rounded-lg hover:bg-dark-700 transition text-gray-300">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCars;